'use client';

import React, { useEffect, useMemo, useState } from 'react';

interface WebsiteUrlInputProps {
  id?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  onChange: (fullUrl: string) => void;
  className?: string;
  inputClassName?: string;
}

function stripProtocol(input: string): string {
  return input.trim().replace(/^https?:\/\/*/i, '');
}

function normalizeToFullUrl(input: string | undefined | null): string {
  const raw = stripProtocol(input);
  if (!raw) return '';
  try {
    const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    return u.toString();
  } catch {
    return `https://${raw}`;
  }
}

function extractDomainFromUrl(input: string | undefined | null): string {
  const raw = (input || '').trim();
  if (!raw) return '';
  try {
    const withProtocol = raw.startsWith('http') ? raw : `https://${raw}`;
    const u = new URL(withProtocol);
    return u.host + (u.pathname && u.pathname !== '/' ? u.pathname : '');
  } catch {
    return stripProtocol(raw);
  }
}

export default function WebsiteUrlInput({ id, label = 'Website', value, placeholder = 'example.com', onChange, className = '', inputClassName = '' }: WebsiteUrlInputProps) {
  const [domainValue, setDomainValue] = useState<string>('');

  const fullUrl = useMemo(() => normalizeToFullUrl(value || ''), [value]);

  useEffect(() => {
    setDomainValue(extractDomainFromUrl(fullUrl));
  }, [fullUrl]);

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextDomain = e.target.value;
    setDomainValue(nextDomain);
    const full = normalizeToFullUrl(nextDomain);
    onChange(full);
  };

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          {label}
        </label>
      ) : null}
      <div
        className={`flex overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-gray-900 focus-within:ring-1 focus-within:ring-gray-900 ${
          label ? 'mt-1' : ''
        }`}
      >
        <div className="inline-flex shrink-0 items-center border-r border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-500">
          https://
        </div>
        <input
          type="url"
          id={id}
          placeholder={placeholder}
          value={domainValue}
          onChange={handleDomainChange}
          className={`block w-full min-w-0 flex-1 border-0 bg-transparent px-4 py-3 text-base font-medium text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 sm:py-3.5 sm:text-sm ${inputClassName}`}
        />
      </div>
    </div>
  );
}
